import React from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { Invitees } from '@atlassian/invite-prompt';
import {
	InviteErrorFlag,
	InvitePromptOnPagePublishFlag,
	InviteSuccessFlag,
} from '@atlassian/invite-prompt';

import type { FlagsStateContainer } from '@confluence/flags';

import {
	increaseDismissalCount,
	setUserOptedOut,
	shouldShowOptOutOption,
} from '../services/feature-visited';
import { inviteUsers } from '../services/invite-users/invite-users';
import {
	fireAddButtonClicked,
	fireFlagDismissed,
	fireFlagShown,
	fireUserOptedOut,
	getExperimentAnalytics,
} from '../services/analytics';

const PRODUCT = 'confluence';
const MAX_NUMBER_OF_INVITEES = 1;

export const showInvitePromptFlag = (
	invitees: Invitees,
	continueUrl: string,
	cloudId: string,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	flags?: FlagsStateContainer,
) => {
	const analytics = getExperimentAnalytics(createAnalyticsEvent);
	const onDismissFlag = () => {
		fireFlagDismissed(analytics);
		increaseDismissalCount();
	};

	const onOptOutFlag = (id: string | number, flags?: FlagsStateContainer) => {
		fireUserOptedOut(analytics);
		flags?.hideFlag(id);
		setUserOptedOut();
	};

	const maxInvitees = invitees.slice(0, MAX_NUMBER_OF_INVITEES);

	const onInviteUsers = async (id: string | number, flags?: FlagsStateContainer) => {
		try {
			await inviteUsers(
				{
					userIds: maxInvitees.map(({ id }) => id),
					continueUrl,
					tenantId: cloudId,
					product: PRODUCT,
				},
				createAnalyticsEvent,
			);
			flags?.hideFlag(id);
			showInviteSuccessFlag(maxInvitees, flags);
		} catch {
			flags?.hideFlag(id);
			showInviteErrorFlag(flags);
		}
		fireAddButtonClicked(analytics);
	};

	flags?.showFlag({
		customComponent: (props) => (
			<InvitePromptOnPagePublishFlag
				{...props}
				id={props.id}
				invitees={maxInvitees}
				onDismiss={() => {
					onDismissFlag();
				}}
				onOptOut={
					shouldShowOptOutOption()
						? () => {
								onOptOutFlag(props.id, flags);
							}
						: undefined
				}
				onSubmit={async () => await onInviteUsers(props.id, flags)}
			/>
		),
	});
	fireFlagShown(analytics);
};

const showInviteSuccessFlag = (invitees: Invitees, flags?: FlagsStateContainer) => {
	const dismiss = (id: string | number) => flags?.hideFlag(id);

	flags?.showFlag({
		customComponent: (props) => (
			<InviteSuccessFlag
				{...props}
				id={props.id}
				invitees={invitees}
				product={PRODUCT}
				onDismiss={dismiss}
			/>
		),
	});
};

const showInviteErrorFlag = (flags?: FlagsStateContainer) => {
	const dismiss = (id: string | number) => flags?.hideFlag(id);
	flags?.showFlag({
		customComponent: (props) => <InviteErrorFlag {...props} id={props.id} onDismiss={dismiss} />,
	});
};
